/**
 * Admin Transactions Management Page
 * View all transactions, filter, search, and export to CSV/PDF
 */

import { useState } from 'react';
import { Search, Download, Filter, Eye, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import AdminLayout from '@/components/AdminLayout';
import { trpc } from '@/lib/trpc';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';

interface Transaction {
  id: string;
  date: string;
  user: string;
  game: string;
  amount: number;
  profit: number;
  status: 'completed' | 'pending' | 'failed' | 'refunded';
  paymentMethod: 'wepay' | 'stripe' | 'promptpay';
  transactionId: string;
}

export default function AdminTransactions() {
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState<'all' | 'completed' | 'pending' | 'failed' | 'refunded'>('all');
  const [filterPayment, setFilterPayment] = useState<'all' | 'wepay' | 'stripe' | 'promptpay'>('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [sortBy, setSortBy] = useState<'date' | 'amount' | 'profit'>('date');
  const [showFilters, setShowFilters] = useState(false);

  // Mock data - TODO: Replace with actual API call
  const [transactions] = useState<Transaction[]>([
    {
      id: 'TXN-001',
      date: '2024-11-01 19:29',
      user: 'user_123',
      game: 'Valorant Promotion',
      amount: 500,
      profit: 50,
      status: 'completed',
      paymentMethod: 'wepay',
      transactionId: 'WP-12345678',
    },
    {
      id: 'TXN-002',
      date: '2024-11-01 15:45',
      user: 'user_456',
      game: 'League of Legends',
      amount: 1000,
      profit: 100,
      status: 'completed',
      paymentMethod: 'stripe',
      transactionId: 'ST-87654321',
    },
    {
      id: 'TXN-003',
      date: '2024-11-01 12:30',
      user: 'user_789',
      game: 'Mobile Legends',
      amount: 250,
      profit: 25,
      status: 'pending',
      paymentMethod: 'promptpay',
      transactionId: 'PP-11223344',
    },
    {
      id: 'TXN-004',
      date: '2024-10-31 10:15',
      user: 'user_101',
      game: 'PUBG Mobile',
      amount: 750,
      profit: 75,
      status: 'completed',
      paymentMethod: 'wepay',
      transactionId: 'WP-55667788',
    },
  ]);

  // Filter transactions
  const filteredTransactions = transactions
    .filter((tx) => {
      if (searchQuery && !tx.transactionId.toLowerCase().includes(searchQuery.toLowerCase()) &&
          !tx.user.toLowerCase().includes(searchQuery.toLowerCase()) &&
          !tx.game.toLowerCase().includes(searchQuery.toLowerCase())) {
        return false;
      }
      if (filterStatus !== 'all' && tx.status !== filterStatus) {
        return false;
      }
      if (filterPayment !== 'all' && tx.paymentMethod !== filterPayment) {
        return false;
      }
      return true;
    })
    .sort((a, b) => {
      if (sortBy === 'date') return new Date(b.date).getTime() - new Date(a.date).getTime();
      if (sortBy === 'amount') return b.amount - a.amount;
      return b.profit - a.profit;
    });

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'bg-green-100 text-green-800';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'failed':
        return 'bg-red-100 text-red-800';
      case 'refunded':
        return 'bg-blue-100 text-blue-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getPaymentMethodColor = (method: string) => {
    switch (method) {
      case 'wepay':
        return 'bg-purple-100 text-purple-800';
      case 'stripe':
        return 'bg-blue-100 text-blue-800';
      case 'promptpay':
        return 'bg-green-100 text-green-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const totalAmount = filteredTransactions.reduce((sum, tx) => sum + tx.amount, 0);
  const totalProfit = filteredTransactions.reduce((sum, tx) => sum + tx.profit, 0);

  const handleExport = () => {
    toast.success('Exporting transactions...');
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold tracking-tight text-black">Transactions</h1>
            <p className="text-black text-sm mt-1">
              View and manage all transactions
            </p>
          </div>
          <div className="flex gap-2">
            <Button onClick={handleExport} variant="outline" size="sm">
              <Download className="w-4 h-4 mr-2" />
              Export
            </Button>
            <Button onClick={() => setShowFilters(!showFilters)} variant="outline" size="sm">
              <Filter className="w-4 h-4 mr-2" />
              Filters
            </Button>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Card className="bg-white border-2 border-black">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-black">Total Amount</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-black">฿{totalAmount.toLocaleString()}</div>
              <p className="text-xs text-black mt-1">{filteredTransactions.length} transactions</p>
            </CardContent>
          </Card>

          <Card className="bg-white border-2 border-black">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-black">Total Profit</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-black">฿{totalProfit.toLocaleString()}</div>
              <p className="text-xs text-black mt-1">{((totalProfit / totalAmount) * 100 || 0).toFixed(1)}% margin</p>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        {showFilters && (
          <Card className="bg-white border-2 border-black">
            <CardContent className="pt-6">
              <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                <div className="relative">
                  <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Search..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10"
                  />
                </div>

                <Select value={filterStatus} onValueChange={(v) => setFilterStatus(v as any)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    <SelectItem value="completed">Completed</SelectItem>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="failed">Failed</SelectItem>
                    <SelectItem value="refunded">Refunded</SelectItem>
                  </SelectContent>
                </Select>

                <Select value={filterPayment} onValueChange={(v) => setFilterPayment(v as any)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Payment" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Methods</SelectItem>
                    <SelectItem value="wepay">WePay</SelectItem>
                    <SelectItem value="stripe">Stripe</SelectItem>
                    <SelectItem value="promptpay">PromptPay</SelectItem>
                  </SelectContent>
                </Select>

                <Input
                  type="date"
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                  placeholder="From"
                />

                <Input
                  type="date"
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                  placeholder="To"
                />
              </div>
            </CardContent>
          </Card>
        )}

        {/* Transactions Table */}
        <Card className="bg-white border-2 border-black">
          <CardHeader>
            <CardTitle className="text-black">Transaction List</CardTitle>
            <CardDescription className="text-black">Total: {filteredTransactions.length} transactions</CardDescription>
          </CardHeader>
          <CardContent>
            {filteredTransactions.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-3 px-4 font-medium text-black">Transaction ID</th>
                      <th className="text-left py-3 px-4 font-medium text-black">Date</th>
                      <th className="text-left py-3 px-4 font-medium text-black">User</th>
                      <th className="text-left py-3 px-4 font-medium text-black">Game</th>
                      <th className="text-left py-3 px-4 font-medium text-black">Amount</th>
                      <th className="text-left py-3 px-4 font-medium text-black">Profit</th>
                      <th className="text-left py-3 px-4 font-medium text-black">Payment</th>
                      <th className="text-left py-3 px-4 font-medium text-black">Status</th>
                      <th className="text-left py-3 px-4 font-medium text-black">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredTransactions.map((tx) => (
                      <tr key={tx.id} className="border-b hover:bg-gray-100">
                        <td className="py-3 px-4 font-mono text-xs text-black">{tx.transactionId}</td>
                        <td className="py-3 px-4 text-xs text-black">{tx.date}</td>
                        <td className="py-3 px-4 text-xs text-black">{tx.user}</td>
                        <td className="py-3 px-4 text-black">{tx.game}</td>
                        <td className="py-3 px-4 font-medium text-black">฿{tx.amount.toLocaleString()}</td>
                        <td className="py-3 px-4 font-medium text-green-600">฿{tx.profit.toLocaleString()}</td>
                        <td className="py-3 px-4">
                          <Badge className={getPaymentMethodColor(tx.paymentMethod)}>
                            {tx.paymentMethod.toUpperCase()}
                          </Badge>
                        </td>
                        <td className="py-3 px-4">
                          <Badge className={getStatusColor(tx.status)}>
                            {tx.status}
                          </Badge>
                        </td>
                        <td className="py-3 px-4">
                          <Button size="sm" variant="ghost">
                            <Eye className="w-4 h-4" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="py-8 text-center text-black">
                No transactions found
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
}
